import React from 'react';
import { type VariantProps } from 'class-variance-authority';
import { Loader2, PlusIcon, FilterIcon, PencilIcon, UploadIcon, ChevronDownIcon, ChevronUpIcon, ArrowRightIcon, Trash2Icon } from 'lucide-react';
import { Button, buttonVariants } from 'shadcn-ui';
import { ButtonColor, ButtonLayoutProps, ButtonSize, IconType } from 'ui-builder';

type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type ButtonSizeVariant = NonNullable<VariantProps<typeof buttonVariants>['size']>;

function mapColorToVariant(color?: ButtonColor, isFilled?: boolean): ButtonVariant {
  switch (color) {
    case ButtonColor.GREEN:
      return 'success';
    case ButtonColor.BLUE:
    case ButtonColor.DARK_BLUE:
      return 'default';
    case ButtonColor.RED:
      return isFilled ? 'destructive' : 'destructive-outline';
    case ButtonColor.GRAY:
      return 'secondary';
    case ButtonColor.BLUE_LIGHT:
      return 'outline';
    case ButtonColor.WHITE:
      return 'ghost';
    case ButtonColor.YELLOW:
      return 'default';
    default:
      return isFilled ? 'default' : 'outline';
  }
}

function mapSize(size?: ButtonSize): ButtonSizeVariant {
  switch (size) {
    case ButtonSize.SMALL:
      return 'sm';
    case ButtonSize.MEDIUM:
      return 'default';
    case ButtonSize.BIG:
      return 'default';
    default:
      return 'default';
  }
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  [IconType.PLUS]: PlusIcon,
  [IconType.FILTER]: FilterIcon,
  [IconType.PENCIL]: PencilIcon,
  [IconType.UPLOAD]: UploadIcon,
  [IconType.ARROW_DOWN]: ChevronDownIcon,
  [IconType.ARROW_UP]: ChevronUpIcon,
  [IconType.ARROW_RIGHT]: ArrowRightIcon,
  [IconType.TRASH]: Trash2Icon,
};

export default function ShadcnButtonLayout(props: ButtonLayoutProps) {
  const variant = mapColorToVariant(props.color, props.isFilled);
  const size = mapSize(props.size);
  const IconComponent = props.iconType ? ICON_MAP[props.iconType] : undefined;

  return (
    <Button
      type={props.isSubmitButton ? 'submit' : 'button'}
      variant={variant}
      size={size}
      disabled={!!props.isDisabled}
      onClick={(e) => {
        e.preventDefault();
        if (props.onClick) {
          props.onClick();
        }
      }}
      ref={props.elementRef}
      title={props.title}
      data-testid={props.testId}
      className={props.className}
    >
      {props.isLoading && <Loader2 className="animate-spin" />}
      {!props.isLoading && props.image && (
        <span className="size-6 flex items-center justify-center shrink-0">
          {props.image}
        </span>
      )}
      {!props.isLoading && IconComponent && <IconComponent className="size-4" />}
      {props.text}
    </Button>
  );
}
